import { useCallback, useEffect, useRef, useState } from 'react'
import { FaFolderClosed, FaHouse } from 'react-icons/fa6'
import { FolderView } from './FolderView'
import { useIntl } from 'react-intl'
import { ModalDocument } from './Modals/modalDocument'
import Knowledge from '../../../../../API/Knowledge/knowledge'
import Loading from '../../../../../Components/Loading/Loading'
import CreateFolder from './Modals/createFolder'
import ModalFilter from './Modals/ModalFilter'
import { getEmployeesURL } from '../../../../../API/Employee/Employee'

export const KnowledgeManagement = () => {
	const token = localStorage.getItem('token')
	const user = JSON.parse(localStorage.getItem('user'))
	const isEmployee = window.location.pathname.includes('/employee/')
	const companyId = isEmployee ? user.companyId : user.id
	const getLanguage = useIntl()
	const language = getLanguage.locale === 'PT-BR' ? 'pt' : 'es'
	const [loading, setLoading] = useState(false)
	const [folders, setFolders] = useState([])
	const [folderId, setFolderId] = useState(null)
	const [isModalDocumentOpen, setIsModalDocumentOpen] = useState(false)
	const [isModalFolderOpen, setIsModalFolderOpen] = useState(false)
	const [tagList, setTagList] = useState([])
	const [employeesImages, setEmployeesImages] = useState([])

	const [isModalFilterOpen, setIsModalFilterOpen] = useState(false)
	const [search, setSearch] = useState({ author: '', document: '', tag: [] })
	const [tagDropdownOpen, setTagDropdownOpen] = useState(false)
	const [tagSearch, setTagSearch] = useState('')
	const tagDropdownRef = useRef(null)

	const currentFolders = folders.filter(f => (folderId === null ? f.parentId === null : f.parentId === folderId))
	const currentFolder = folders.find(f => f.id === folderId)
	const currentDocuments = currentFolder?.documents || []

	const loadData = useCallback(async () => {
		try {
			setLoading(true)
			const [respTagList, respFolders, respEmployeeImages] = await Promise.all([
				Knowledge.getTagList(),
				Knowledge.getFolderList(companyId),
				getEmployeesURL(companyId, token),
			])
			setTagList(respTagList.sort((a, b) => a.name.localeCompare(b.name)))
			setFolders(respFolders)
			setEmployeesImages(respEmployeeImages)
		} finally {
			setLoading(false)
		}
	}, [companyId, token])

	useEffect(() => {
		loadData()
	}, [loadData])

	useEffect(() => {
		function handleClickOutside(event) {
			if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
				setTagDropdownOpen(false)
			}
		}
		if (tagDropdownOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		} else {
			document.removeEventListener('mousedown', handleClickOutside)
		}
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [tagDropdownOpen])

	const formatDate = timestamp => {
		const date = new Date(timestamp)
		const str = date.toLocaleDateString(language === 'pt' ? 'pt-BR' : 'es-CL', {
			day: '2-digit',
			month: 'long',
			year: 'numeric',
		})
		return str.charAt(0).toUpperCase() + str.slice(1)
	}

	const getFolderPath = (id, list) => {
		const path = []
		const map = new Map(list.map(f => [f.id, f]))
		let currentId = id

		while (currentId) {
			const folder = map.get(currentId)
			if (!folder) break
			path.unshift(folder)
			currentId = folder.parentId
		}
		return path
	}

	const handleDelete = useCallback(
		async id => {
			setLoading(true)
			try {
				await Knowledge.deleteDocument(id)
				await loadData()
			} catch (error) {
				console.error('Error:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadData]
	)

	const handleDeleteFolder = useCallback(
		async id => {
			setLoading(true)
			try {
				await Knowledge.deleteFolder(id)
				await loadData()
			} catch (error) {
				console.error('Error:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadData]
	)

	const handleMove = (folderId, newParentId) => {
		setFolders(folders => folders.map(f => (f.folderId === folderId ? { ...f, parentId: newParentId } : f)))
	}

	const handleLike = async doc => {
		const user = JSON.parse(localStorage.getItem('user'))
		const likeKey = `liked_doc_${doc.id}_user_${user.id}`
		if (sessionStorage.getItem(likeKey)) return

		setFolders(prev =>
			prev.map(folder => ({
				...folder,
				documents: folder.documents.map(d => (d.id === doc.id ? { ...d, likesCount: (d.likesCount || 0) + 1 } : d)),
			}))
		)
		sessionStorage.setItem(likeKey, '1')
		await Knowledge.likeDocument(doc.id)
	}

	const handleView = async doc => {
		setFolders(prev =>
			prev.map(folder => ({
				...folder,
				documents: folder.documents.map(d => (d.id === doc.id ? { ...d, viewsCount: (d.viewsCount || 0) + 1 } : d)),
			}))
		)
		await Knowledge.viewDocument(doc.id)
	}

	const handleFavorite = async doc => {
		const newValue = !doc.highlightedByHr
		setFolders(prev =>
			prev.map(folder => ({
				...folder,
				documents: folder.documents.map(d => (d.id === doc.id ? { ...d, highlightedByHr: newValue } : d)),
			}))
		)
		await Knowledge.favoriteDocument(doc.id)
	}

	const handleDocumentSubmit = useCallback(
		async (data, file) => {
			setLoading(true)
			try {
				const uploadRoutes = await Knowledge.uploadDocument([data])
				const uploadRoute = uploadRoutes[0].url
				await fetch(uploadRoute, {
					method: 'PUT',
					body: file,
					headers: { 'Content-Type': file.type },
				})

				await loadData()
				setIsModalDocumentOpen(false)
			} catch (error) {
				console.error('Error to submit document:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadData]
	)

	const handleCreateFolder = useCallback(
		async data => {
			setLoading(true)
			try {
				await Knowledge.createFolder(data)

				await loadData()
				setIsModalFolderOpen(false)
			} catch (error) {
				console.error('Error to create folder:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadData]
	)

	const pathChain = getFolderPath(folderId, folders)

	const handleSearchChange = e => {
		const { name, value } = e.target
		setSearch(prev => ({ ...prev, [name]: value }))
	}

	const handleTagToggle = tagId => {
		setSearch(prev => {
			const alreadySelected = prev.tag.includes(tagId)
			return {
				...prev,
				tag: alreadySelected ? prev.tag.filter(id => id !== tagId) : [...prev.tag, tagId],
			}
		})
	}

	const handleTagSearchChange = e => {
		setTagSearch(e.target.value)
	}

	const handleTagDropdown = () => {
		setTagDropdownOpen(open => !open)
		setTagSearch('')
	}

	const isSearchEnabled =
		(search.author && search.author.trim() !== '') || (search.document && search.document.trim() !== '') || (search.tag && search.tag.length > 0)

	const handleSearch = () => {
		setIsModalFilterOpen(true)
	}

	const handleCloseFilter = () => {
		setIsModalFilterOpen(false)
	}

	return (
		<>
			{loading ? (
				<Loading />
			) : (
				<>
					<div className='tw-flex tw-items-start tw-justify-between tw-mb-4 tw-pr-10 2xl:tw-pr-10 xl:tw-pr-6 lg:tw-pr-4 md:tw-pr-2'>
						<div>
							<h3 className='fw-bold tw-mb-4 tw-text-2xl xl:tw-text-xl lg:tw-text-lg md:tw-text-base 2xl:tw-text-2xl'>Gestión de Conocimientos</h3>
							<div className='tw-text-base xl:tw-text-sm md:tw-text-xs 2xl:tw-text-base'>
								Organiza y comparte documentos esenciales de la empresa, ya sean corporativos, por departamento o personales, asegurando que cada
								colaborador acceda solo a lo que le corresponde.
							</div>
						</div>
						<div className='tw-flex tw-gap-2 tw-flex-nowrap'>
							{!isEmployee && (
								<div
									className='border tw-flex-nowrap tw-px-4 tw-py-2 tw-text-gray-700 hover:tw-bg-gray-50 tw-cursor-pointer tw-rounded-lg tw-whitespace-nowrap tw-text-base xl:tw-text-sm md:tw-text-xs'
									onClick={() => setIsModalFolderOpen(true)}
								>
									Criar nueva carpeta
								</div>
							)}
							<div
								className='tw-bg-purple-500 tw-flex-nowrap tw-text-white tw-px-4 tw-py-2 hover:tw-bg-purple-700 tw-cursor-pointer tw-rounded-lg tw-whitespace-nowrap tw-text-base xl:tw-text-sm md:tw-text-xs'
								onClick={() => setIsModalDocumentOpen(true)}
							>
								Subir nuevo documento
							</div>
						</div>
					</div>

					<div className='tw-mb-6 tw-flex tw-gap-4 tw-items-center tw-pr-10 2xl:tw-pr-10 xl:tw-pr-6 lg:tw-pr-4 md:tw-pr-2'>
						<div className='tw-flex tw-flex-col'>
							<input
								type='text'
								name='author'
								placeholder='Autor'
								value={search.author}
								onChange={handleSearchChange}
								className='border tw-rounded-md tw-px-3 tw-py-2 tw-w-full tw-text-base xl:tw-text-sm md:tw-text-xs'
								style={{ borderRadius: 6, height: 42 }}
							/>
						</div>
						<div className='tw-flex tw-flex-col'>
							<input
								type='text'
								name='document'
								placeholder='Documento'
								value={search.document}
								onChange={handleSearchChange}
								className='border tw-rounded-md tw-px-3 tw-py-2 tw-w-full tw-text-base xl:tw-text-sm md:tw-text-xs'
								style={{ borderRadius: 6, height: 42 }}
							/>
						</div>
						<div className='tw-flex tw-flex-col tw-relative' ref={tagDropdownRef}>
							<div
								className='border tw-rounded-md tw-px-3 tw-py-2 tw-bg-white tw-cursor-pointer tw-text-base xl:tw-text-sm md:tw-text-xs'
								style={{ minWidth: 180, width: 220, maxWidth: 220, borderRadius: 6, height: 42, display: 'flex', alignItems: 'center' }}
								onClick={handleTagDropdown}
							>
								{search.tag.length === 0
									? 'Tags'
									: `${search.tag.length} tags${search.tag.length > 1 ? 's' : ''} seleccionada${search.tag.length > 1 ? 's' : ''}`}
							</div>
							{tagDropdownOpen && (
								<div
									className='tw-absolute tw-z-20 tw-bg-white border tw-rounded-lg tw-mt-3 tw-shadow-lg tw-w-full tw-max-h-56 tw-overflow-y-auto tw-px-2'
									style={{ top: '110%' }}
								>
									<input
										type='text'
										placeholder='Buscar tags...'
										value={tagSearch}
										onChange={handleTagSearchChange}
										className='tw-w-full border tw-px-3 tw-py-2 tw-rounded-md tw-text-base xl:tw-text-sm md:tw-text-xs'
										style={{ borderRadius: 6, height: 38 }}
									/>
									<div className='tw-flex tw-flex-wrap tw-gap-2 tw-p-2'>
										{tagList
											.filter(tag => tag.name.toLowerCase().includes(tagSearch.toLowerCase()))
											.map(tag => {
												const selected = search.tag.includes(tag.id)
												return (
													<span
														key={tag.id}
														className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-cursor-pointer ${
															selected ? 'tw-bg-purple-500 tw-text-white' : 'tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-purple-100'
														}`}
														onClick={() => handleTagToggle(tag.id)}
													>
														{tag.name}
													</span>
												)
											})}
									</div>
								</div>
							)}
						</div>
						<button
							className={`tw-bg-purple-500 tw-text-white tw-px-4 tw-py-2 tw-rounded-md hover:tw-bg-purple-700 border-0 tw-text-base xl:tw-text-sm md:tw-text-xs ${
								!isSearchEnabled ? 'tw-opacity-50 tw-cursor-not-allowed' : ''
							}`}
							onClick={handleSearch}
							disabled={!isSearchEnabled}
							style={{ borderRadius: 6, height: 42 }}
						>
							Buscar
						</button>
						<button
							className='tw-bg-gray-200 tw-text-gray-700 tw-px-4 tw-py-2 tw-rounded-md hover:tw-bg-gray-300 border-0 tw-text-base xl:tw-text-sm md:tw-text-xs'
							onClick={() => setSearch({ author: '', document: '', tag: [] })}
							style={{ borderRadius: 6, height: 42 }}
						>
							Limpiar
						</button>
					</div>

					<nav className='tw-flex tw-items-center tw-gap-2 tw-text-base xl:tw-text-sm md:tw-text-xs tw-text-gray-500 tw-mt-4 tw-pr-10 2xl:tw-pr-10 xl:tw-pr-6 lg:tw-pr-4 md:tw-pr-2'>
						<span
							onClick={() => setFolderId(null)}
							className='tw-cursor-pointer tw-text-purple-500 hover:tw-text-indigo-600 tw-flex tw-items-center tw-gap-2'
						>
							<FaHouse />
							<span>Inicio</span>
						</span>
						{pathChain.map((folder, i) => {
							const isLast = i === pathChain.length - 1
							return (
								<span key={folder.id} className='tw-flex tw-items-center tw-gap-2'>
									<span>/</span>
									<span
										className={!isLast ? 'tw-text-purple-500 tw-cursor-pointer hover:tw-text-indigo-600' : ''}
										onClick={!isLast ? () => setFolderId(folder.id) : undefined}
									>
										{folder.name}
									</span>
								</span>
							)
						})}
					</nav>

					<FolderView
						folders={currentFolders}
						documents={currentDocuments}
						onOpen={setFolderId}
						formatDate={formatDate}
						onDeleteDocument={handleDelete}
						onDeleteFolder={handleDeleteFolder}
						onMove={handleMove}
						onLike={handleLike}
						onFavorite={handleFavorite}
						isHR={isEmployee ? false : true}
						onView={handleView}
						isEmployee={isEmployee}
						employeesImages={employeesImages}
					/>

					<CreateFolder isOpen={isModalFolderOpen} onClose={() => setIsModalFolderOpen(false)} folders={folders} onCreated={handleCreateFolder} />

					<ModalDocument
						onSubmit={handleDocumentSubmit}
						isOpen={isModalDocumentOpen}
						folders={folders}
						onClose={() => setIsModalDocumentOpen(false)}
						tagList={tagList}
						initialFolderId={folderId}
					/>

					<ModalFilter
						companyId={companyId}
						isOpen={isModalFilterOpen}
						onClose={handleCloseFilter}
						author={search.author}
						document={search.document}
						tag={search.tag}
					/>
				</>
			)}
		</>
	)
}
